import { isStripeEnabled } from "common";
import { useEffect } from "react";
import { DelegateRole, FeatureRole, Permission, Plugin, Seller, User, UserRole, UserType } from "types";
import {
    canBookPastDates,
    hasAnyFeature,
    hasFeature,
    isAuthorizeNetEnabled,
    isGratuityEnabled,
    isQrCodeEnabled,
    isShowTopUp,
    isStripeXola,
} from "../models/Seller";
import { hasAnyRole, hasOnlyRoles, hasRole, isAffiliate, isWareManagerOnly } from "../models/User";
import { useSeller, useUser } from "./useAccount";
import { useInstalledPlugins } from "./usePlugins";
import { setInstalledPluginsInStore } from "@/stores/globalStore";

export const useAuthorization = () => {
    const seller = useSeller();
    const user = useUser();
    const { data: plugins, isLoading: isLoadingPlugins } = useInstalledPlugins();

    // TODO: Intentionally using the seller's roles in place of the user's,
    // since user's roles array is empty when the user is logged in as a delegate.
    // Also, all user's roles/permissions are appended to the seller object anyway.
    const permissionMap = getPermissionMap(seller, user, plugins);

    useEffect(() => {
        setInstalledPluginsInStore(plugins, !isLoadingPlugins);
    }, [plugins, isLoadingPlugins]);

    const isGranted = (permission: Permission) => {
        if (permissionMap[permission] === undefined) {
            console.error(`Rules for permission "${permission}" are not defined`);
        }

        return permissionMap[permission] ?? false;
    };

    const isGrantedAll = (...permissions: Permission[]) => {
        return permissions.every((permission) => isGranted(permission));
    };

    const isGrantedAny = (...permissions: Permission[]) => {
        return permissions.some((permission) => isGranted(permission));
    };

    // No need to export both `isGranted` and `isGrantedAll`.
    return { isGranted: isGrantedAll, isGrantedAny };
};

export const hasPluginAbility = (plugins: Plugin[], ability: string) => {
    return plugins.some((plugin) => plugin.abilities?.[ability]);
};

type PermissionMap = {
    [permissionType: string]: boolean;
};

// TODO: Consider breaking down into smaller pieces

const getPermissionMap = (seller: Seller, user: User, plugins: Plugin[]): PermissionMap => {
    return {
        /**
         * DASHBOARD_VIEW
         */
        [Permission.DASHBOARD_VIEW]: hasAnyRole(user, [
            DelegateRole.GUIDE_MANAGER,
            DelegateRole.GUIDE,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        /**
         * DASHBOARD_SEARCH_VIEW
         */
        [Permission.DASHBOARD_SEARCH_VIEW]: hasAnyRole(user, [
            DelegateRole.GUIDE_MANAGER,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        [Permission.DASHBOARD_AVAILABILITY_VIEW]: hasAnyRole(user, [
            DelegateRole.GUIDE_MANAGER,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        [Permission.MODIFY_EVENT]: hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.RESERVATION_LITE]),

        [Permission.SEND_EVENT_MESSAGE]: hasAnyRole(user, [DelegateRole.RESERVATION]),

        [Permission.MODIFY_CAPACITY]: hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.ADD_EVENT_NOTE]:
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.RESERVATION_LITE, DelegateRole.GUIDE]) &&
            !(
                hasOnlyRoles(user, [DelegateRole.GUIDE_MANAGER]) ||
                hasOnlyRoles(user, [DelegateRole.GUIDE_MANAGER, DelegateRole.GUIDE])
            ),

        [Permission.MODIFY_NOTES]: hasAnyRole(user, [
            UserRole.ADMIN,
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.GUEST_STATUS]: hasAnyRole(user, [
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.EQUIPMENT_MANAGER,
            DelegateRole.GUIDE,
        ]),

        // This includes modifying the guests, add/remove guests and questionnaire
        [Permission.MODIFY_GUEST_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.AFFILIATE,
        ]),

        [Permission.EMAIL_ROSTER]: hasRole(user, DelegateRole.GUIDE_MANAGER),

        [Permission.MESSAGE_DAY_RESERVATION_VIEW]: hasAnyRole(user, [
            UserRole.ADMIN,
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
        ]),

        [Permission.REFUND_AMOUNT]: hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.MODIFY_COUPONS]: hasRole(user, DelegateRole.MARKETER),

        [Permission.APPLY_COUPONS]: hasAnyRole(user, [
            DelegateRole.MARKETER,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.SELLER_ADMIN,
        ]),

        [Permission.MODIFY_PARTNERS]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.MODIFY_TERMS_TEMPLATES]:
            (hasFeature(seller, FeatureRole.AFFILIATES) || hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING)) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.MODIFY_PARTNERS_BIO]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER, DelegateRole.AFFILIATE]),

        [Permission.ALLOW_PAST_DATES]:
            hasAnyRole(user, [
                UserRole.ADMIN,
                DelegateRole.SELLER_ADMIN,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
            ]) && canBookPastDates(seller),
        /**
         * PURCHASES_VIEW
         */
        [Permission.PURCHASES_VIEW]:
            hasAnyRole(user, [
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
                DelegateRole.AFFILIATE,
                DelegateRole.ACCOUNTANT,
            ]) && !hasOnlyRoles(user, [DelegateRole.AFFILIATE, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.ADD_BOOKING_VIEW]: hasAnyRole(user, [
            DelegateRole.AFFILIATE,
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.MODIFY_BOOKING]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.AFFILIATE,
        ]),

        [Permission.SMS_SEND]: hasPluginAbility(plugins, "sms"),

        /**
         * CUSTOMERS_VIEW
         */
        [Permission.CUSTOMERS_VIEW]: hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.MARKETER]),

        [Permission.CUSTOMERS_EXPORT]: hasAnyRole(user, [DelegateRole.MARKETER, DelegateRole.ACCOUNTANT]),

        [Permission.STORE_CREDIT_VIEW]: hasFeature(seller, FeatureRole.GIFT),

        [Permission.MODIFY_CUSTOMER_DETAILS]: hasRole(user, DelegateRole.RESERVATION),

        [Permission.WAIVER_TIMELINE_VIEW]:
            hasFeature(seller, FeatureRole.WAIVER) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        /**
         * MESSAGE_TEMPLATES_VIEW
         */
        [Permission.MESSAGE_TEMPLATES_VIEW]: hasRole(user, DelegateRole.RESERVATION),

        /**
         * NOTIFICATIONS_VIEW
         */
        [Permission.NOTIFICATIONS_VIEW]: hasRole(user, DelegateRole.RESERVATION),

        /**
         * NOTIFICATIONS_DISMISS_VIEW
         */
        [Permission.NOTIFICATIONS_DISMISS_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
        ]),

        /**
         * BALANCE_VIEW
         */
        [Permission.BALANCE_VIEW]: !isAffiliate(user) && hasAnyRole(user, [DelegateRole.ACCOUNTANT]),

        /**
         * PRODUCTS_VIEW
         */
        [Permission.PRODUCTS_VIEW]: hasAnyRole(user, [DelegateRole.CURATOR, DelegateRole.WARE_MANAGER]),

        /**
         * PRODUCT_CATALOG_VIEW
         */
        [Permission.PRODUCT_CATALOG_VIEW]: hasRole(user, DelegateRole.CURATOR),

        [Permission.PRODUCT_GLOBAL_SETTINGS_REVIEW_TAB_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.PRODUCT_GLOBAL_SETTINGS_DATEPICKER_TAB_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.PRODUCT_GLOBAL_SETTINGS_EMAIL_FOOTER_TAB_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.PRODUCT_GLOBAL_SETTINGS_CHECKIN_PREFERENCE_TAB_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        /**
         * GIFT_OFFERS_VIEW
         */
        // [Permission.GIFT_OFFERS_VIEW]:
        //     hasFeature(seller, FeatureRole.GIFT) && hasAnyRole(user, [DelegateRole.CURATOR, DelegateRole.MARKETER]),

        [Permission.GIFT_OFFERS_VIEW]:
            ("gift" in seller.preferences && seller.preferences?.gift?.enabled && !isAffiliate(user)) ?? false,

        /**
         * MULTI_LANGUAGE_SUPPORT_VIEW
         */
        [Permission.MULTI_LANGUAGE_SUPPORT_VIEW]:
            hasFeature(seller, FeatureRole.MULTI_LANGUAGE_SUPPORT) && hasRole(user, DelegateRole.CURATOR),
        [Permission.MULTI_LANGUAGE_SUPPORT_ADD_LANGUAGE]:
            hasFeature(seller, FeatureRole.MULTI_LANGUAGE_SUPPORT) && hasRole(user, DelegateRole.SELLER_ADMIN),

        /**
         * HARDWARE_VIEW
         */
        [Permission.HARDWARE_VIEW]:
            (hasFeature(seller, FeatureRole.EMV) ||
                hasFeature(seller, FeatureRole.POS) ||
                hasFeature(seller, FeatureRole.TICKETS)) &&
            hasAnyRole(user, [
                DelegateRole.SELLER_ADMIN,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
                DelegateRole.AFFILIATE,
            ]),

        [Permission.PAYMENT_TERMINAL_VIEW]:
            hasFeature(seller, FeatureRole.EMV) &&
            hasAnyRole(user, [
                DelegateRole.SELLER_ADMIN,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
                DelegateRole.AFFILIATE,
            ]),

        [Permission.EDIT_PAIR_PAYMENT_DEVICE]:
            hasFeature(seller, FeatureRole.EMV) &&
            hasAnyRole(user, [
                DelegateRole.SELLER_ADMIN,
                DelegateRole.AFFILIATE,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
            ]),

        [Permission.RECEIPT_PRINTING_VIEW]:
            hasFeature(seller, FeatureRole.POS) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.RESERVATION]),

        [Permission.RECEIPT_PRINTING_ADVANCED_EDIT]:
            hasFeature(seller, FeatureRole.POS) && hasRole(user, UserRole.ADMIN),

        [Permission.TICKET_PRINTING_VIEW]:
            hasFeature(seller, FeatureRole.TICKETS) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.RESERVATION, DelegateRole.RESERVATION_LITE]),

        [Permission.TICKET_PRINTING_SELLER_VIEW]:
            hasFeature(seller, FeatureRole.TICKETS) && hasAnyRole(user, [DelegateRole.SELLER_ADMIN, UserRole.ADMIN]),

        /**
         * PACKAGES_VIEW
         */
        [Permission.PACKAGES_VIEW]: hasFeature(seller, FeatureRole.PACKAGES),
        [Permission.PACKAGES_MODIFY]: hasFeature(seller, FeatureRole.PACKAGES) && hasRole(user, DelegateRole.CURATOR),

        /**
         * MEMBERSHIPS_VIEW
         */
        [Permission.MEMBERSHIPS_VIEW]: hasFeature(seller, FeatureRole.MEMBERSHIPS),
        [Permission.MEMBERSHIPS_MODIFY]:
            hasFeature(seller, FeatureRole.MEMBERSHIPS) && hasRole(user, DelegateRole.CURATOR),

        /**
         * MERCHANDISE_VIEW
         */
        [Permission.MERCHANDISE_VIEW]: hasFeature(seller, FeatureRole.MERCHANDISE),
        [Permission.MERCHANDISE_MODIFY]: hasFeature(seller, FeatureRole.MERCHANDISE),
        [Permission.MERCHANDISE_PURCHASE_VIEW]:
            hasFeature(seller, FeatureRole.MERCHANDISE) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.WARE_MANAGER]),

        // Features hidden from Merchandise-Manager-only delegates (e.g. taxes & fees, full report list).
        [Permission.NON_WARE_MANAGER_VIEW]: !isWareManagerOnly(user),

        /**
         * PARTNER_EXPERIENCES
         */
        [Permission.PARTNER_EXPERIENCES_VIEW]:
            hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.RESERVATION_LITE]),
        /**
         * PARTNER_PACKAGES_VIEW
         */
        [Permission.PARTNER_PACKAGES_VIEW]: hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING) && !isAffiliate(user),
        [Permission.PARTNER_PACKAGES_MODIFY]:
            hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING) && hasRole(user, DelegateRole.CURATOR),

        /**
         * MODIFY_RESOURCES_EQUIPMENT
         */
        [Permission.MODIFY_RESOURCES_EQUIPMENT]:
            hasFeature(seller, FeatureRole.SELLER_INVENTORY) && hasRole(user, DelegateRole.EQUIPMENT_MANAGER),

        [Permission.MODIFY_EXPERIENCE_RESOURCES]:
            hasFeature(seller, FeatureRole.SELLER_INVENTORY) &&
            hasAnyRole(user, [DelegateRole.EQUIPMENT_MANAGER, DelegateRole.CURATOR]),

        [Permission.MODIFY_EQUIPMENT_CAPACITY]: hasAnyRole(user, [DelegateRole.RESERVATION]),
        [Permission.MODIFY_EQUIPMENT_CAPACITY_BLACKOUT]: hasAnyRole(user, [DelegateRole.CURATOR]),

        [Permission.VIEW_RESOURCES_EQUIPMENT]:
            hasFeature(seller, FeatureRole.SELLER_INVENTORY) &&
            hasAnyRole(user, [
                DelegateRole.EQUIPMENT_MANAGER,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
                DelegateRole.GUIDE_MANAGER,
                DelegateRole.GUIDE,
            ]),

        /**
         * RESOURCES_EQUIPMENT_SETTINGS_VIEW
         */
        [Permission.RESOURCES_EQUIPMENT_SETTINGS_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            UserRole.SUPER_ADMIN,
            UserRole.ADMIN,
            UserRole.SELLER,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        [Permission.VIEW_RESOURCES_GUIDE]:
            hasFeature(seller, FeatureRole.GUIDE_MANAGER) &&
            hasAnyRole(user, [
                DelegateRole.EQUIPMENT_MANAGER,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
                DelegateRole.GUIDE_MANAGER,
            ]),

        /**
         * MODIFY_RESOURCES_GUIDE
         */
        [Permission.MODIFY_RESOURCES_GUIDE]:
            hasFeature(seller, FeatureRole.GUIDE_MANAGER) && hasRole(user, DelegateRole.GUIDE_MANAGER),

        [Permission.RESOURCES_GUIDES_ALL_VIEW]:
            hasFeature(seller, FeatureRole.GUIDE_MANAGER) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.GUIDE_MANAGER]),

        [Permission.RESOURCES_GUIDES_PARTIAL_VIEW]:
            hasFeature(seller, FeatureRole.GUIDE_MANAGER) &&
            hasAnyRole(user, [
                DelegateRole.SELLER_ADMIN,
                DelegateRole.GUIDE_MANAGER,
                DelegateRole.GUIDE,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
            ]),

        /**
         * GUIDE_PROFILE_VIEW
         */
        [Permission.GUIDE_PROFILE_VIEW]:
            hasFeature(seller, FeatureRole.GUIDE_MANAGER) && hasRole(user, DelegateRole.GUIDE, true),

        /**
         * TRANSACTIONS_VIEW
         */
        [Permission.TRANSACTIONS_VIEW]: hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.ACCOUNTANT]),

        /**
         * REPORTS_ANALYTICS_VIEW
         */
        [Permission.REPORTS_ANALYTICS_VIEW]:
            hasFeature(seller, FeatureRole.ANALYTICS) && hasRole(user, DelegateRole.ACCOUNTANT),

        /**
         * REPORTS_CUSTOMERS_VIEW
         */
        [Permission.REPORTS_CUSTOMERS_VIEW]:
            hasFeature(seller, FeatureRole.ANALYTICS) && hasRole(user, DelegateRole.ACCOUNTANT),

        /**
         * REPORTS_EARNINGS_VIEW
         */
        [Permission.REPORTS_EARNINGS_VIEW]:
            hasFeature(seller, FeatureRole.ANALYTICS) && hasRole(user, DelegateRole.ACCOUNTANT),

        /**
         * REPORTS_EARNINGS_VIEW
         */
        [Permission.REPORTS_GIFT_OFFERS_VIEW]:
            (hasFeature(seller, FeatureRole.ANALYTICS) &&
                hasFeature(seller, FeatureRole.GIFT) &&
                hasRole(user, DelegateRole.ACCOUNTANT) &&
                "gift" in seller.preferences &&
                seller.preferences?.gift?.enabled) ??
            false,

        /**
         * REPORTS_CUSTOM_REPORTS_VIEW
         */
        [Permission.REPORTS_CUSTOM_REPORTS_VIEW]:
            (hasFeature(seller, FeatureRole.ANALYTICS) &&
                hasFeature(seller, FeatureRole.REPORT_TEMPLATES) &&
                hasRole(user, DelegateRole.ACCOUNTANT)) ||
            // Ware managers get Custom Reports for the Merchandise Inventory report only.
            (hasFeature(seller, FeatureRole.MERCHANDISE) && hasRole(user, DelegateRole.WARE_MANAGER, true)),

        /**
         * REPORTS_XOLABOT_VIEW
         */
        [Permission.REPORTS_XOLABOT_VIEW]:
            hasPluginAbility(plugins, "xolabot") && hasRole(user, DelegateRole.ACCOUNTANT),

        /**
         * REPORTS_TICKET_REDEMPTION_VIEW
         */
        [Permission.REPORTS_TICKET_REDEMPTION_VIEW]: hasPluginAbility(plugins, "reporting"),

        /**
         * RESOURCES_VIEW
         * This includes 2 sections for modifying equipment and other for guides.
         */
        [Permission.RESOURCES_VIEW]: hasAnyFeature(seller, [FeatureRole.SELLER_INVENTORY, FeatureRole.GUIDE_MANAGER]),

        /**
         * WAITLISTS_VIEW
         */
        [Permission.WAITLISTS_VIEW]: hasFeature(seller, FeatureRole.WAITLIST),

        /**
         * WAIVERS_VIEW
         */
        [Permission.WAIVERS_VIEW]:
            hasPluginAbility(plugins, "waiver") &&
            hasAnyRole(user, [
                DelegateRole.SELLER_ADMIN,
                DelegateRole.CURATOR,
                DelegateRole.RESERVATION,
                DelegateRole.RESERVATION_LITE,
                DelegateRole.MARKETER,
            ]),

        [Permission.WAIVERS_PERMISSIONS_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.CURATOR,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.MARKETER,
        ]),

        /**
         * AFFILIATES_VIEW
         */
        [Permission.AFFILIATES_VIEW]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [
                DelegateRole.SELLER_ADMIN,
                DelegateRole.RESERVATION,
                DelegateRole.MARKETER,
                DelegateRole.AFFILIATE,
            ]),

        [Permission.AFFILIATES_ACTIONS]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.RESERVATION, DelegateRole.AFFILIATE]),

        /**
         * ABANDONED_VIEW
         */
        [Permission.ABANDONED_VIEW]:
            hasFeature(seller, FeatureRole.ABANDONS) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.CONVERSION_TRACKING_VIEW]:
            hasAnyFeature(seller, [FeatureRole.FACEBOOK_PIXEL_TRACKING, FeatureRole.CONVERSION_TRACKING]) &&
            hasRole(user, DelegateRole.MARKETER),

        [Permission.OPERATORS_VIEW]:
            hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.OPERATORS_ACTIONS]:
            hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING) && hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.AGENTS_TERMS_TEMPLATE_ACTIONS]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.DISTRIBUTION_PROFILE_VIEW]:
            hasFeature(seller, FeatureRole.CROSS_ACCOUNT_SELLING) && hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.COUPONS_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.XOLABOT_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.FACEBOOK_PIXEL_TRACKING_VIEW]:
            hasFeature(seller, FeatureRole.FACEBOOK_PIXEL_TRACKING) && hasRole(user, DelegateRole.MARKETER),

        [Permission.CUSTOM_CONVERSION_TRACKING_VIEW]:
            hasFeature(seller, FeatureRole.CONVERSION_TRACKING) && hasRole(user, DelegateRole.MARKETER),

        /**
         * SETTINGS_VIEW
         */
        [Permission.SETTINGS_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.EQUIPMENT_MANAGER,
            DelegateRole.MARKETER,
            DelegateRole.GUIDE_MANAGER,
            DelegateRole.WARE_MANAGER,
            DelegateRole.GUIDE,
            DelegateRole.CURATOR,
            DelegateRole.ACCOUNTANT,
            DelegateRole.AFFILIATE,
        ]),

        /**
         * ACCESS_MANAGER_VIEW
         */
        [Permission.ACCESS_MANAGER_VIEW]:
            hasFeature(seller, FeatureRole.ACCESS_MANAGER) && hasRole(user, DelegateRole.SELLER_ADMIN),

        /**
         * MODIFY_ACCESS_MANAGER_MAKE_OWNER
         */
        [Permission.MODIFY_ACCESS_MANAGER_MAKE_OWNER]:
            user.type === UserType.SELLER && hasFeature(seller, FeatureRole.ACCESS_MANAGER),

        /**
         * MODIFY_ACCESS_MANAGER_REVOKE_ACCESS
         */
        [Permission.MODIFY_ACCESS_MANAGER_REVOKE_ACCESS]:
            hasRole(user, DelegateRole.SELLER_ADMIN) && hasFeature(seller, FeatureRole.ACCESS_MANAGER),

        /**
         * MODIFY_ROSTERS_WAIVER_EXPORT_DATA
         */
        [Permission.MODIFY_ROSTERS_WAIVER_EXPORT_DATA]: hasPluginAbility(plugins, "waiver"),

        /**
         * MARKETING_VIEW
         */
        [Permission.MARKETING_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        /**
         * EMBEDDED_CHECKOUT_VIEW
         */
        [Permission.EMBEDDED_CHECKOUT_VIEW]: hasFeature(seller, FeatureRole.EMBEDDED_CHECKOUT),

        /**
         * INVOICE
         */

        [Permission.INVOICE_VIEW]: hasFeature(seller, FeatureRole.INVOICE),

        /**
         * APP_STORE_VIEW
         */
        [Permission.APP_STORE_VIEW]: hasAnyRole(user, [UserRole.ADMIN, DelegateRole.SELLER_ADMIN]),

        /**
         * REPORTS_VIEW
         */
        [Permission.REPORTS_VIEW]:
            ((hasFeature(seller, FeatureRole.ANALYTICS) || hasFeature(seller, FeatureRole.AFFILIATES)) &&
                hasAnyRole(user, [
                    DelegateRole.SELLER_ADMIN,
                    DelegateRole.ACCOUNTANT,
                    DelegateRole.GUIDE,
                    DelegateRole.GUIDE_MANAGER,
                    DelegateRole.AFFILIATE,
                ])) ||
            (hasFeature(seller, FeatureRole.MERCHANDISE) && hasRole(user, DelegateRole.WARE_MANAGER, true)),

        /**
         * GRATUITY_VIEW
         */
        [Permission.GRATUITY_VIEW]:
            isGratuityEnabled(seller) &&
            hasAnyRole(user, [DelegateRole.ACCOUNTANT, DelegateRole.GUIDE_MANAGER, DelegateRole.GUIDE]),

        /**
         * GRATUITY_SETTINGS_VIEW
         */
        [Permission.GRATUITY_SETTINGS_VIEW]: isGratuityEnabled(seller) && hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        [Permission.MODIFY_PARTNERS_TAX_AND_FEE_SETTING]: hasFeature(seller, FeatureRole.AFFILIATES),
        [Permission.VIEW_PARTNERS_TAX_AND_FEE_SETTING]: hasFeature(seller, FeatureRole.AFFILIATES),

        [Permission.ADD_MULTI_FACTOR_AUTHENTICATION_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN]),

        /**
         * X_RAY_VIEW
         */
        [Permission.X_RAY_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.COMPANY_PROFILE_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.COMPANY_BASIC_INFO_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.BUTTON_CODE_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.NOTIFICATIONS_SETTINGS_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.ACCOUNT_INFORMATION_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.ACCOUNT_LANGUAGES_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.PREFERENCES_PURCHASES_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.PREFERENCES_GIFT_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.PREFERENCES_ROSTER_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.GUIDE_MANAGER]),

        [Permission.TAXES_AND_FEES_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.PAYMENTS_SETTINGS_VIEW]: hasAnyRole(user, [
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
            DelegateRole.SELLER_ADMIN,
        ]),

        [Permission.APPS_SETTINGS_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.CURATOR]),

        [Permission.MARKETER_HOME_VIEW]: hasRole(user, DelegateRole.MARKETER),

        [Permission.XSITE_VIEW]:
            hasFeature(seller, FeatureRole.XSITE) &&
            hasAnyRole(user, [DelegateRole.WEBSITE_MANAGER, DelegateRole.SELLER_ADMIN]),

        [Permission.PREFERENCES_SELLER_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.PREFERENCES_KIOSK_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.PREFERENCES_IN_APP_NOTIFICATIONS_VIEW]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.PREFERENCES_SETTINGS_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.GUIDE_MANAGER,
        ]),

        [Permission.PREFERENCES_ADMIN_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
        ]),

        [Permission.PREFERENCES_ROSTER_AUTO_EXPAND_NOTES]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
        ]),

        [Permission.PREFERENCES_ROSTER_PRINT_NOTES]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
        ]),

        /**
         *  Purchase Action Buttons
         */

        [Permission.MODIFY_BOOKING_LITE]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.AFFILIATE,
        ]),

        [Permission.RESEND_WAIVER_EMAIL]:
            hasPluginAbility(plugins, "waiver") &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.RESERVATION, DelegateRole.RESERVATION_LITE]),

        [Permission.RESEND_GRATUITY_EMAIL]:
            isGratuityEnabled(seller) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.RESERVATION, DelegateRole.RESERVATION_LITE]),

        [Permission.PRINT_RECEIPT]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.EXPORT_TICKETS]: !hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.ACCOUNTANT,
        ]),

        [Permission.PURCHASE_TAGS_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.AFFILIATE,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        [Permission.MODIFY_PURCHASE_CONTACT_VIEW]: hasAnyRole(user, [
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.AFFILIATE,
        ]),

        [Permission.PURCHASE_NOTES_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.AFFILIATE,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        [Permission.MODIFY_WAIVER_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
            DelegateRole.AFFILIATE,
            DelegateRole.EQUIPMENT_MANAGER,
        ]),

        /**
         * Split Payment Settings
         */
        [Permission.SPLIT_PAYMENT_VIEW]: hasFeature(seller, FeatureRole.SPLIT_PAYMENT),

        /**
         * SERVER_SIDE_TRACKING_VIEW
         */
        [Permission.SERVER_SIDE_TRACKING_VIEW]:
            hasFeature(seller, FeatureRole.TELEMETRY) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.MARKETER]),

        [Permission.REPORTS_AFFILIATE_COMMISSIONS_VIEW]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.AFFILIATE, DelegateRole.ACCOUNTANT]),

        [Permission.REPORTS_PAYOUTS_VIEW]: hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.ACCOUNTANT]),
        [Permission.MARKETING_MANAGE_CHILDREN_VIEW]: hasAnyRole(user, [
            UserRole.ADMIN,
            DelegateRole.SELLER_ADMIN,
            DelegateRole.AFFILIATE,
            DelegateRole.ACCOUNTANT,
        ]),

        [Permission.QRCODE_VIEW]: isQrCodeEnabled(seller),

        [Permission.EDIT_REFUND_RESERVE]: hasRole(user, DelegateRole.SELLER_ADMIN),

        [Permission.USER_PROFILE_VIEW]: !hasRole(user, UserRole.ADMIN),

        [Permission.AMOUNT_DUE_VIEW]: hasAnyRole(user, [
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.DEMOGRAPHIC_LIST_VIEW]: hasAnyRole(user, [
            DelegateRole.CURATOR,
            DelegateRole.MARKETER,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.VIRTUAL_MEETINGS_VIEW]: hasPluginAbility(plugins, "virtualMeeting"),

        [Permission.SEARCH_CUSTOMERS]: hasAnyRole(user, [
            UserRole.ADMIN,
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.TOP_UP_VIEW]: isShowTopUp(seller),

        [Permission.MODIFY_TAXES_AND_FEES]: hasAnyRole(user, [
            UserRole.ADMIN,
            DelegateRole.SELLER_ADMIN,
            DelegateRole.RESERVATION,
            DelegateRole.RESERVATION_LITE,
        ]),

        [Permission.PARTNERS_COMMERCIAL_TERMS_VIEW]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.PARTNERS_PURCHASE_SETTINGS_VIEW]:
            hasFeature(seller, FeatureRole.AFFILIATES) &&
            hasAnyRole(user, [DelegateRole.RESERVATION, DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.AGENTS_EXPERIENCES_SETTINGS_VIEW]:
            hasFeature(seller, FeatureRole.AFFILIATES) && hasAnyRole(user, [DelegateRole.EQUIPMENT_MANAGER]),

        [Permission.ROSTER_VIEW]: !hasRole(user, DelegateRole.AFFILIATE),

        [Permission.TUTORIALS]: hasFeature(seller, FeatureRole.TUTORIALS),

        [Permission.XOLA_ADMIN]: user.type === 3 && user.roles.includes(UserRole.ADMIN),

        [Permission.X2_CHECKOUT_VIEW]:
            user.roles.includes(UserRole.ADMIN) &&
            hasFeature(seller, FeatureRole.X2_CHECKOUT) &&
            (isStripeEnabled(seller) || isAuthorizeNetEnabled(seller)),

        [Permission.MODIFY_COLLECTIONS]:
            hasFeature(seller, FeatureRole.PRODUCT_COLLECTIONS) &&
            hasAnyRole(user, [DelegateRole.SELLER_ADMIN, DelegateRole.CURATOR]),

        [Permission.DISPUTE_REPORT_VIEW]: isStripeXola(seller) && hasRole(user, DelegateRole.ACCOUNTANT),

        [Permission.DISPUTE_CHALLENGE_VIEW]:
            isStripeXola(seller) && hasAnyRole(user, [UserRole.SELLER, DelegateRole.SELLER_ADMIN]),

        [Permission.CUSTOMERS_SETTINGS_VIEW]: hasAnyRole(user, [UserRole.SELLER, DelegateRole.SELLER_ADMIN]),
    };
};
